import { AxiosResponse } from "axios";
import { httpRequest } from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { Lead, LeadsResponse, LeadFiltersProps } from "@/types";

// Função para buscar leads
async function fetchLeads(
  filters?: LeadFiltersProps
): Promise<LeadsResponse> {
  const params = new URLSearchParams();

  if (filters?.page) params.append('page', filters.page);
  if (filters?.per_page) params.append('per_page', filters.per_page.toString());
  if (filters?.search) params.append('search', filters.search);
  if (filters?.source) params.append('source', filters.source);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.company) params.append('company', filters.company);
  if (filters?.start_date) params.append('start_date', filters.start_date);
  if (filters?.end_date) params.append('end_date', filters.end_date);
  if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);
  if (filters?.converted_to_patient) params.append('converted_to_patient', filters.converted_to_patient);

  const leads = await httpRequest.get<LeadsResponse>(
    `/clinic/crm/leads${params.toString() ? `?${params.toString()}` : ''}`
  );
  return leads.data;
}

// Hook para buscar leads paginados
export function useFetchLeadsQuery(filters?: LeadFiltersProps) {
  const leadsQuery = useQuery({
    queryKey: ["leads", filters],
    queryFn: () => fetchLeads(filters),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    ...leadsQuery,
    data: leadsQuery?.data,
  };
}

async function fetchLeadsNoPaginate(): Promise<AxiosResponse<Lead[]>> {
  return await httpRequest.get<Lead[]>(`/clinic/crm/leads`);
}

export function useFetchLeadsNoPaginateQuery() {
  const leadsQuery = useQuery({
    queryKey: ["leads", "list"],
    queryFn: () => fetchLeadsNoPaginate(),
    keepPreviousData: true,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });

  return {
    ...leadsQuery,
    data: leadsQuery?.data?.data,
  };
}

// Função para buscar lead por ID
async function fetchLeadById(id: string): Promise<AxiosResponse<Lead>> {
  return await httpRequest.get<Lead>(`/clinic/crm/leads/${id}`);
}

export function useFetchLeadByIdQuery(id: string) {
  const leadQuery = useQuery({
    queryKey: ["leads", id],
    queryFn: () => fetchLeadById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    ...leadQuery,
    data: leadQuery?.data?.data,
  };
}